import apiAxios from "../../../services/apiAxios";
import {
    setSearchResults,
    setSearchLoading,
    setHealthMapItems,
    setHealthMapLoading,
    setHealthMapError,
    setSelectedPatient,
    addHealthMapItem,
    updateHealthMapItem,
    removeHealthMapItem,
} from "../../Reducer/HealthMap/HealthMapSlice";
import toast from "react-hot-toast";

// Search patients globally
export const searchHealthMapPatients = (query) => async (dispatch) => {
    try {
        dispatch(setSearchLoading(true));
        const { data } = await apiAxios.get(
            `api/healthmap/patients/search?q=${encodeURIComponent(query)}`
        );
        dispatch(setSearchResults(data.patients || []));
    } catch (error) {
        console.error("Error searching patients:", error);
        dispatch(setSearchResults([]));
    } finally {
        dispatch(setSearchLoading(false));
    }
};

// Fetch full cross-doctor HealthMap for a patient
export const fetchHealthMap = (PID) => async (dispatch) => {
    try {
        dispatch(setHealthMapLoading(true));
        dispatch(setHealthMapError(null));
        const { data } = await apiAxios.get(`api/healthmap/${PID}`);
        if (data.success) {
            dispatch(setSelectedPatient(data.patient));
            dispatch(setHealthMapItems(data.healthmap || []));
        }
    } catch (error) {
        console.error("Error fetching HealthMap:", error);
        dispatch(setHealthMapError(error?.response?.data?.error || error.message));
        toast.error("Failed to load HealthMap");
    } finally {
        dispatch(setHealthMapLoading(false));
    }
};

// Create a custom HealthMap note
export const createHealthMapNote = (formData) => async (dispatch, getState) => {
    try {
        const { data } = await apiAxios.post("api/healthmap/note", formData, {
            headers: { "Content-Type": "multipart/form-data" }
        });
        if (data.success) {
            // Add to healthmap with doctor info from current session
            const doctorDetails = getState()?.doctor?.doctorDetails?.doctor;
            const personalInfo = getState()?.doctor?.doctorDetails?.doctor?.personalInfo;
            const doctorName = personalInfo
                ? `Dr. ${personalInfo.name}${personalInfo.surname ? " " + personalInfo.surname : ""}`
                : "Doctor";

            dispatch(addHealthMapItem({
                type: "note",
                id: data.note.id,
                DID: doctorDetails?.DID,
                doctorName,
                createdAt: data.note.createdAt,
                updatedAt: data.note.updatedAt,
                data: { 
                    content: data.note.content,
                    visibility: data.note.visibility,
                    imageUrl: data.note.imageUrl
                },
            }));
            toast.success("HealthMap note saved");
        }
    } catch (error) {
        console.error("Error creating healthmap note:", error);
        toast.error("Failed to save note");
    }
};

// Update a custom HealthMap note
export const updateHealthMapNote = ({ id, formData }) => async (dispatch) => {
    try {
        const { data } = await apiAxios.put(`api/healthmap/note/${id}`, formData, {
            headers: { "Content-Type": "multipart/form-data" }
        });
        if (data.success) {
            dispatch(updateHealthMapItem({
                id,
                type: "note",
                data: {
                    data: { 
                        content: data.note.content,
                        visibility: data.note.visibility,
                        imageUrl: data.note.imageUrl
                    },
                    updatedAt: data.note.updatedAt,
                },
            }));
            toast.success("HealthMap note updated");
        }
    } catch (error) {
        console.error("Error updating healthmap note:", error);
        toast.error(error?.response?.data?.error || "Failed to update note");
    }
};

// Delete a HealthMap note
export const deleteHealthMapNote = (id) => async (dispatch) => {
    try {
        const { data } = await apiAxios.delete(`api/healthmap/note/${id}`);
        if (data.success) {
            dispatch(removeHealthMapItem({ id, type: "note" }));
            toast.success("HealthMap note deleted");
            return true;
        }
    } catch (error) {
        console.error("Error deleting healthmap note:", error);
        toast.error(error?.response?.data?.error || "Failed to delete note");
        return false;
    }
};

// Create a custom HealthMap report
export const createHealthMapReport = (formData) => async (dispatch, getState) => {
    try {
        const { data } = await apiAxios.post("api/healthmap/report", formData, {
            headers: { "Content-Type": "multipart/form-data" }
        });
        if (data.success) {
            const doctorDetails = getState()?.doctor?.doctorDetails?.doctor;
            const personalInfo = getState()?.doctor?.doctorDetails?.doctor?.personalInfo;
            const doctorName = personalInfo
                ? `Dr. ${personalInfo.name}${personalInfo.surname ? " " + personalInfo.surname : ""}`
                : "Doctor";

            dispatch(addHealthMapItem({
                type: "report",
                id: data.report.id,
                DID: doctorDetails?.DID,
                doctorName,
                createdAt: data.report.createdAt,
                updatedAt: data.report.updatedAt,
                data: { 
                    title: data.report.title,
                    description: data.report.description,
                    fileUrl: data.report.fileUrl,
                    fileName: data.report.fileName,
                    fileType: data.report.fileType,
                    visibility: data.report.visibility
                },
            }));
            toast.success("HealthMap report saved");
        }
    } catch (error) {
        console.error("Error creating healthmap report:", error);
        toast.error("Failed to save report");
    }
};

// Update a custom HealthMap report
export const updateHealthMapReport = ({ id, formData }) => async (dispatch) => {
    try {
        const { data } = await apiAxios.put(`api/healthmap/report/${id}`, formData, {
            headers: { "Content-Type": "multipart/form-data" }
        });
        if (data.success) {
            dispatch(updateHealthMapItem({
                id,
                type: "report",
                data: {
                    data: { 
                        title: data.report.title,
                        description: data.report.description,
                        fileUrl: data.report.fileUrl,
                        fileName: data.report.fileName,
                        fileType: data.report.fileType,
                        visibility: data.report.visibility
                    },
                    updatedAt: data.report.updatedAt,
                },
            }));
            toast.success("HealthMap report updated");
        }
    } catch (error) {
        console.error("Error updating healthmap report:", error);
        toast.error(error?.response?.data?.error || "Failed to update report");
    }
};

// Delete a HealthMap report
export const deleteHealthMapReport = (id) => async (dispatch) => {
    try {
        const { data } = await apiAxios.delete(`api/healthmap/report/${id}`);
        if (data.success) {
            dispatch(removeHealthMapItem({ id, type: "report" }));
            toast.success("HealthMap report deleted");
            return true;
        }
    } catch (error) {
        console.error("Error deleting healthmap report:", error);
        toast.error(error?.response?.data?.error || "Failed to delete report");
        return false;
    }
};
